import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { MembershipType, PersonOrgType } from '@ndvr/customer-api';

import { getPersonName, useRequiredParams } from 'utils';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';
import {
    FormInput,
    FormSection,
    FormSelect,
    FormYesNoToggle,
    PageSpinner,
    Ribbon,
    Title,
    VerticalLayout,
} from 'widgets';

import { useClientProfilesForHousehold } from './client-queries';

export const JointFormFields = ({ isEditing }: { isEditing: boolean }) => (
    <VerticalLayout $gap={24}>
        <FormSection title="Joint Details" hasDivider>
            <FormInput
                name="orgName"
                placeholder="Name"
                label="Joint name"
                description="Auto-fill based on selected Owners"
            />
            <FormYesNoToggle name="highNetWorth" label="High Net Worth" />
        </FormSection>
        <FormSection hasDivider>
            <Title content>Joint Owners</Title>
            <MemberSelector disabled={isEditing} />
        </FormSection>
    </VerticalLayout>
);

const MemberSelector = ({ disabled }: { disabled: boolean }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: personOrgs, isLoading } = usePersonOrgProfilesForHousehold(globalAccountGUID);
    const { data: accountClients } = useClientProfilesForHousehold(globalAccountGUID);

    const {
        clearErrors,
        setValue,
        setError,
        formState: { errors },
    } = useFormContext();

    const jointOwners = useWatch({ name: 'jointOwners' });

    useEffect(() => {
        if (!disabled && jointOwners?.[MembershipType.JOINT_PRIMARY] && jointOwners?.[MembershipType.JOINT_SECONDARY]) {
            const primaryOwner = personOrgs?.find(
                (personOrgProfile) =>
                    personOrgProfile.personOrg.personOrgGUID === jointOwners[MembershipType.JOINT_PRIMARY],
            );
            const secondaryOwner = personOrgs?.find(
                (personOrgProfile) =>
                    personOrgProfile.personOrg.personOrgGUID === jointOwners[MembershipType.JOINT_SECONDARY],
            );
            setValue(
                'orgName',
                `${getPersonName(primaryOwner?.personOrg)} & ${getPersonName(secondaryOwner?.personOrg)} joint`,
                { shouldValidate: true },
            );

            const existingClient = accountClients?.find((accountClient) => {
                if (accountClient.clientPersonOrgProfile.personOrg.personOrgType !== PersonOrgType.JOINT_PERSONS) {
                    return false;
                }

                const primaryMatch = accountClient.memberProfiles.find(
                    (memberProfile) =>
                        memberProfile.link.membershipType === MembershipType.JOINT_PRIMARY &&
                        memberProfile.link.memberPersonOrgGUID === jointOwners[MembershipType.JOINT_PRIMARY],
                );
                const secondaryMatch = accountClient.memberProfiles.find(
                    (memberProfile) =>
                        memberProfile.link.membershipType === MembershipType.JOINT_SECONDARY &&
                        memberProfile.link.memberPersonOrgGUID === jointOwners[MembershipType.JOINT_SECONDARY],
                );

                return !!primaryMatch && !!secondaryMatch;
            });

            if (existingClient) {
                setError('client-already-exists', { message: 'Client already exists' });
            } else {
                clearErrors('client-already-exists');
            }
        }
    }, [accountClients, clearErrors, disabled, jointOwners, personOrgs, setError, setValue]);

    const availablePersons = personOrgs?.filter(
        (personOrgProfile) => personOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON,
    );

    if (isLoading) {
        return <PageSpinner />;
    }

    return (
        <VerticalLayout $gap={16}>
            <FormSelect name={`jointOwners.${MembershipType.JOINT_PRIMARY}`} label="Primary" disabled={disabled}>
                <option></option>
                {availablePersons?.map(({ personOrg }) => (
                    <option key={personOrg.personOrgGUID} value={personOrg.personOrgGUID}>
                        {getPersonName(personOrg)}
                    </option>
                ))}
            </FormSelect>
            <FormSelect name={`jointOwners.${MembershipType.JOINT_SECONDARY}`} label="Secondary" disabled={disabled}>
                <option></option>
                {availablePersons?.map(({ personOrg }) => (
                    <option key={personOrg.personOrgGUID} value={personOrg.personOrgGUID}>
                        {getPersonName(personOrg)}
                    </option>
                ))}
            </FormSelect>
            {!disabled && errors?.['client-already-exists'] && (
                <Ribbon intent="warning">Client with the same primary and secondary owners already exist</Ribbon>
            )}
        </VerticalLayout>
    );
};
